import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { PARTNER_QUERY_KEY, usePartnerDetail } from "../../lib/apis/hooks/partner.hook";
import { deletePartner } from "../../lib/apis/v1/partner.service";
import { partnerPageUrl } from "../../lib/apis/dtos/request/partner.request";
import { getQueryClient } from "../../config/queryClient.config";
import SuccessSnackbar from "../../components/ui/snackbar/success.component";
import FailSnackbar from "../../components/ui/snackbar/fail.component";
import ExplainTitle from "../../components/layout/header/explain-title";
import Button from "../../components/ui/button";
import Error403 from "../../components/layout/errors/403";
import { useModalStore } from "../../store/useModalStore";
import { MODAL_KEY } from "../../lib/constants/modal.constants";
import { formatDateTime } from "../../utils/date.utils";

interface InfoRowProps {
  label: string;
  children: React.ReactNode;
}

function InfoRow({ label, children }: InfoRowProps) {
  return (
    <tr className="content-table-tr">
      <td className="content-table-th">{label}</td>
      <td className="content-table-td flex-row gap-4">{children}</td>
    </tr>
  );
}

interface ImageRowProps {
  label: string;
  src: string | null | undefined;
  alt: string;
}

/** 로고/배너 표시 — 업로드·삭제는 "제휴 업체 수정" 모달에서 */
function ImageRow({ label, src, alt }: ImageRowProps) {
  return (
    <InfoRow label={label}>
      {src ? (
        <img src={src} alt={alt} className="h-[80px] w-auto max-w-[320px] object-contain bg-[#F0F3F4] p-2" />
      ) : (
        <span>-</span>
      )}
    </InfoRow>
  );
}

function ExternalLink({ href }: { href: string | null | undefined }) {
  if (!href) return <span>-</span>;
  return (
    <a href={href} target="_blank" rel="noreferrer" className="text-[#1976D2] underline break-all">
      {href}
    </a>
  );
}

export default function PartnerDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const partnerId = id ?? "";
  const { openModal } = useModalStore();

  const { data, isSuccess } = useQuery({ ...usePartnerDetail(partnerId) });
  const { result, code } = data || {};

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePartner(id),
    onSuccess: (res) => {
      if (res.error) {
        FailSnackbar(res.message || "제휴 업체 삭제 실패");
        return;
      }
      SuccessSnackbar("제휴 업체 삭제 완료");
      getQueryClient().invalidateQueries({ queryKey: [PARTNER_QUERY_KEY.list] });
      navigate("/partner");
    },
    onError: () => FailSnackbar("제휴 업체 삭제 실패"),
  });

  if (code === 403) return <Error403 />;

  return (
    <div className="p-6">
      <ExplainTitle
        backOnclick={() => navigate("/partner")}
        title={result?.partnerName || "-"}
        explanation="제휴 업체 정보를 관리하세요."
        titleSuffix={
          <div className="flex-row gap-2 pl-2">
            <Button
              variant={result?.isActive ? "primary" : "gray"}
              label={result?.isActive ? "활성" : "비활성"}
              className="px-[10px] h-8 rounded-[100px] text-[13px]"
            />
          </div>
        }
      >
        <div className="flex-row justify-end gap-2 w-full">
          <Button
            variant="outline"
            label="시세표 편집"
            disabled={!result}
            onClick={() => navigate(`/partner/quotation/${partnerId}`)}
          />
          <Button
            variant="primary"
            label="제휴 업체 수정"
            disabled={!isSuccess || !result}
            onClick={() => result && openModal(MODAL_KEY.PartnerCreate, { partner: result })}
          />
          <Button
            variant="outline-red"
            label="제휴 업체 삭제"
            disabled={!result || deleteMutation.isPending}
            onClick={() =>
              openModal(MODAL_KEY.Confirm, {
                title: "제휴 업체 삭제",
                content: `${result?.partnerName} 업체를 삭제하시겠습니까? 연결 링크가 더 이상 동작하지 않습니다.`,
                rightButton: {
                  title: "삭제",
                  variant: "danger",
                  onClick: () => deleteMutation.mutate(partnerId),
                },
              })
            }
          />
        </div>
      </ExplainTitle>

      <table className="w-full">
        <tbody>
          <InfoRow label="연결 링크">
            <ExternalLink href={partnerPageUrl(result?.link, result?.partnerType)} />
            {result?.link && <span className="text-black/50">({result.link})</span>}
          </InfoRow>
          <InfoRow label="지점 유형">
            <span>{result?.partnerTypeName || result?.partnerType || "-"}</span>
          </InfoRow>
          <InfoRow label="공급업체">
            <span>{result?.referenceStore?.name || "-"}</span>
          </InfoRow>
          <InfoRow label="활성화 기간">
            <span>
              {result?.activeFrom || "-"} ~ {result?.activeTo || "-"}
            </span>
            <span className="text-black/60">
              (설정 {result?.checkActive ? "켬" : "꺼짐"} · 현재 {result?.isActive ? "활성" : "비활성"})
            </span>
          </InfoRow>
          <InfoRow label="상담 링크">
            <ExternalLink href={result?.contactLink} />
          </InfoRow>
          <ImageRow label="로고 이미지" src={result?.logoImageUrl} alt={`${result?.partnerName ?? ""} 로고`} />
          <ImageRow
            label="카카오톡 공유 이미지"
            src={result?.shareImageUrl}
            alt={`${result?.partnerName ?? ""} 카카오톡 공유`}
          />
          <ImageRow label="배너 이미지" src={result?.bannerImageUrl} alt={`${result?.partnerName ?? ""} 배너`} />
          <InfoRow label="시세표 제출">
            <span>{result?.todaySubmitted ? "오늘 제출 완료" : "오늘 미제출"}</span>
            <span className="text-black/60">
              최근 제출 {result?.recentSubmitted ? formatDateTime(result.recentSubmitted) : "-"}
              {result?.isAuto ? " (자동)" : ""}
              {result?.publisherName ? ` · ${result.publisherName}` : ""}
            </span>
          </InfoRow>
        </tbody>
      </table>
    </div>
  );
}
